import type { Location } from "./types";

/**
 * CORRIDOR LOCATIONS — wave 2 of the roster.
 *
 * These close the sideways mesh that `core.ts` and `acreage.ts` reference, and
 * they carry the Bow Valley and the outlying town hubs in their own right.
 *
 * Same standard as every other entry: signal 1 complete and sourced, three
 * airspace-fact beats, at least two location-specific FAQs, and no hook shared
 * with a neighbour. Nothing here is a filler page — an entry that could not
 * clear the gate honestly was left for a later wave rather than padded.
 */
export const CORRIDOR_LOCATIONS: Location[] = [
  {
    slug: "exshaw",
    name: "Exshaw",
    region: "bow-valley",
    city: "Exshaw",
    province: "Alberta",
    coordinates: { lat: 51.0603, lng: -115.1697 },
    tier: 3,
    driveMinutesFromCalgary: 65,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Canmore municipal heliport",
      distanceToAerodromeKm: 17,
      authorizationRequired: false,
      authorizationNote:
        "Exshaw sits in uncontrolled Class G airspace in the lower Bow Valley, outside the national park boundary. The park line to the west is plotted on every flight plan.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
      restrictedZones: ["Banff National Park boundary (west)"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Exshaw is a working industrial hamlet — a cement plant and quarry operating directly beneath some of the most photographed mountain scenery in Alberta.",
      consequence:
        "That mix is the whole visual story here, and a frame that crops the industry out is selling a place that does not exist.",
      resolution:
        "Industrial and landscape subjects are composed together deliberately, which is what makes Exshaw imagery useful for operations reporting as well as marketing.",
    },
    landmarks: ["Highway 1A", "Lac des Arcs", "the Bow River", "Grotto Mountain", "Exshaw Creek"],
    terrain:
      "The lower Bow Valley floor, hemmed by steep front-range walls with the river running through and a large industrial footprint on the north side.",
    windNote:
      "One of the windiest stretches of the Bow corridor — the valley narrows here and accelerates westerly flow markedly.",
    seasonalWindow:
      "The valley loses direct sun early against the south wall. Winter gives the cleanest contrast between snow, rock and industrial structure.",
    bylawNote:
      "M.D. of Bighorn land-use rules apply, and the Banff National Park prohibition begins a short distance west of the hamlet.",
    subjectMix: ["industrial-yard", "municipal-infrastructure", "tourism-hospitality"],
    mobilisationNote:
      "About 65 minutes west of the Calgary base on the Trans-Canada. Scheduled against the valley wind, which is the binding constraint.",
    faqs: [
      {
        question: "Can you fly a drone in Exshaw?",
        answer:
          "Yes. Exshaw sits in uncontrolled Class G airspace outside Banff National Park, so no NAV CANADA authorisation is normally required. The park boundary a short distance west is plotted on every flight plan before takeoff.",
      },
      {
        question: "Why is Exshaw useful for industrial aerial documentation?",
        answer:
          "Because the site is large, active and hard to read from ground level. Stockpiles, haul routes and plant layout resolve into a single legible frame from altitude, which is what makes the imagery usable for operations reporting rather than only marketing.",
      },
    ],
    primaryKeywords: [
      "drone photography Exshaw",
      "Exshaw industrial aerial photography",
      "Bow Valley industrial drone survey",
    ],
    nearestLocations: ["lac-des-arcs", "canmore", "dead-mans-flats", "harvie-heights", "morley"],
  },

  {
    slug: "lac-des-arcs",
    name: "Lac des Arcs",
    region: "bow-valley",
    city: "Lac des Arcs",
    province: "Alberta",
    coordinates: { lat: 51.0489, lng: -115.1289 },
    tier: 4,
    driveMinutesFromCalgary: 62,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Canmore municipal heliport",
      distanceToAerodromeKm: 20,
      authorizationRequired: false,
      authorizationNote:
        "Uncontrolled Class G airspace in the lower Bow Valley, outside the national park. The park boundary west is plotted before every flight.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Lac des Arcs is a shallow widening of the Bow that goes almost dry in some seasons and mirrors the front ranges in others.",
      consequence:
        "The same address photographed three months apart can show open water or a gravel flat, which makes shoot timing a commercial decision rather than a scheduling one.",
      resolution:
        "Water level is checked before the date is set, so a lakeside property is photographed when the lake is actually there.",
    },
    landmarks: ["Lac des Arcs", "the Bow River", "Highway 1A", "Heart Mountain", "the Trans-Canada"],
    terrain:
      "A wide, shallow valley basin where the Bow slows and spreads, ringed by steep front-range walls on both sides.",
    windNote:
      "Notoriously exposed. The basin sits where the valley narrows, and westerly wind funnels across the open water unchecked.",
    seasonalWindow:
      "Water level drives everything. Late spring and early summer give the fullest basin and the strongest reflections.",
    bylawNote:
      "M.D. of Bighorn rules govern the hamlet and the shoreline reserve around the basin.",
    subjectMix: ["lakefront", "tourism-hospitality"],
    mobilisationNote:
      "About 62 minutes from the Calgary base, immediately off the Trans-Canada. Wind, not distance, decides the day.",
    faqs: [
      {
        question: "Can you fly a drone at Lac des Arcs?",
        answer:
          "Yes. The basin sits in uncontrolled Class G airspace outside Banff National Park, so no authorisation is normally required. Wind is the practical constraint — the basin is one of the most exposed points in the Bow corridor.",
      },
      {
        question: "Does Lac des Arcs always have water in it?",
        answer:
          "No, and that matters for a shoot. The basin is a shallow widening of the Bow that can drop to gravel flats in some seasons. Water level is confirmed before the date is set rather than discovered on arrival.",
      },
    ],
    primaryKeywords: [
      "drone photography Lac des Arcs",
      "Lac des Arcs aerial photography",
      "Bow Valley basin drone video",
    ],
    nearestLocations: ["exshaw", "dead-mans-flats", "canmore", "harvie-heights", "morley"],
  },

  {
    slug: "dead-mans-flats",
    name: "Dead Man's Flats",
    region: "bow-valley",
    city: "Dead Man's Flats",
    province: "Alberta",
    coordinates: { lat: 51.0472, lng: -115.2519 },
    tier: 4,
    driveMinutesFromCalgary: 70,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Canmore municipal heliport",
      distanceToAerodromeKm: 9,
      authorizationRequired: false,
      authorizationNote:
        "Uncontrolled Class G airspace just east of Canmore. The Canmore heliport circuit and the park boundary further west are both plotted before flight.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
      restrictedZones: ["Canmore municipal heliport circuit"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Dead Man's Flats is the last uncontrolled, unrestricted valley floor before the Canmore heliport circuit and the national park boundary begin to close options westward.",
      consequence:
        "For a client who wants Bow Valley mountain scale without a permit conversation, this is where the legal ground actually runs out.",
      resolution:
        "It is the default alternative offered when a shot list asks for park scenery that cannot legally be flown.",
    },
    landmarks: ["the Trans-Canada Highway", "Pigeon Mountain", "the Bow River", "Bow Valley Trail"],
    terrain:
      "A narrow valley bench between the Bow River and the front-range wall, with Pigeon Mountain rising immediately south.",
    windNote:
      "Strong valley funnelling, with wind accelerating as the corridor narrows toward Canmore.",
    seasonalWindow:
      "Early light is blocked by the south wall. Late afternoon gives the best separation between the valley floor and the ranges.",
    bylawNote:
      "M.D. of Bighorn land-use rules apply; the Banff National Park prohibition begins west of Canmore.",
    subjectMix: ["tourism-hospitality", "resort-terrain", "commercial-retail"],
    mobilisationNote:
      "About 70 minutes from the Calgary base, immediately off the Trans-Canada.",
    faqs: [
      {
        question: "Can you fly a drone at Dead Man's Flats?",
        answer:
          "Yes. It sits in uncontrolled Class G airspace east of Canmore and outside the national park, so no authorisation is normally required. The Canmore heliport circuit to the west is plotted on the flight plan.",
      },
      {
        question: "Why is Dead Man's Flats offered as an alternative to park locations?",
        answer:
          "Because it is the closest legal ground with genuine Bow Valley mountain scale. When a shot list asks for scenery inside Banff National Park that cannot lawfully be flown, this is the honest substitute rather than a quiet omission.",
      },
    ],
    primaryKeywords: [
      "drone photography Dead Man's Flats",
      "Dead Man's Flats aerial video",
      "Bow Valley legal drone locations",
    ],
    nearestLocations: ["canmore", "lac-des-arcs", "exshaw", "harvie-heights", "kananaskis-village"],
  },

  {
    slug: "harvie-heights",
    name: "Harvie Heights",
    region: "bow-valley",
    city: "Harvie Heights",
    province: "Alberta",
    coordinates: { lat: 51.1075, lng: -115.4014 },
    tier: 4,
    driveMinutesFromCalgary: 80,
    airspace: {
      classification: "Class G uncontrolled, immediately adjacent to Banff National Park",
      nearestAerodrome: "Canmore municipal heliport",
      distanceToAerodromeKm: 6,
      authorizationRequired: false,
      authorizationNote:
        "Harvie Heights sits in uncontrolled airspace but directly against the Banff National Park boundary. The park line is metres away on the western side and is the governing constraint on every flight here.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
      restrictedZones: ["Banff National Park boundary (immediately west)"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Harvie Heights sits directly on the Banff National Park boundary — the park line runs along the edge of the hamlet itself.",
      consequence:
        "An airframe drifting a few hundred metres west crosses from legal to prohibited without any visible change on the ground.",
      resolution:
        "Flights here are flown with the boundary as a hard geofenced limit rather than a line on a map, which is the only responsible way to work this close to it.",
    },
    landmarks: [
      "the Trans-Canada Highway",
      "Mount Lady Macdonald",
      "the Banff Park gate",
      "Harvie Heights Road",
    ],
    terrain:
      "A wooded bench at the foot of the front ranges, pressed against the park boundary at the head of the Bow Valley.",
    windNote:
      "Sheltered relative to the open valley below, but subject to sudden downslope gusts off the ranges above.",
    seasonalWindow:
      "The surrounding peaks shorten the usable window at both ends of the day more than anywhere else in the corridor.",
    bylawNote:
      "M.D. of Bighorn rules govern the hamlet; Parks Canada's prohibition applies immediately west of the boundary.",
    subjectMix: ["tourism-hospitality", "acreage-estate"],
    mobilisationNote:
      "About 80 minutes from the Calgary base, at the top of the Bow Valley.",
    faqs: [
      {
        question: "Can you fly a drone at Harvie Heights?",
        answer:
          "Yes, with care. The hamlet is in uncontrolled airspace outside Banff National Park, but the park boundary runs along its western edge. Flights are flown against a hard geofenced limit rather than an estimated line.",
      },
      {
        question: "How close is Harvie Heights to the Banff park boundary?",
        answer:
          "Immediately adjacent — the boundary runs along the edge of the hamlet. There is no visible marker on the ground, which is precisely why the limit is set in the flight controller rather than judged by eye.",
      },
    ],
    primaryKeywords: [
      "drone photography Harvie Heights",
      "Harvie Heights aerial photography",
      "flying a drone near the Banff park boundary",
    ],
    nearestLocations: ["canmore", "banff", "dead-mans-flats", "exshaw", "lac-des-arcs"],
  },

  {
    slug: "langdon",
    name: "Langdon",
    region: "rocky-view-east",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 50.9744, lng: -113.6717 },
    tier: 3,
    driveMinutesFromCalgary: 35,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 32,
      authorizationRequired: false,
      authorizationNote:
        "Langdon sits east of the Calgary control zone in uncontrolled Class G airspace. The western edge of the county runs closer to the boundary, so the specific parcel is confirmed before flight.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Langdon sits just far enough east of Calgary to fall outside the control zone while still being a half-hour commute from the city.",
      consequence:
        "That combination is why acreage development moved here, and why a parcel's road access matters as much to a buyer as the house does.",
      resolution:
        "A plan-view pass showing the parcel against the road grid answers the access question directly, in one frame.",
    },
    landmarks: ["Boulder Creek", "Township Road 240", "Highway 797", "Centre Street Langdon"],
    terrain:
      "Flat prairie on the survey grid east of the city, with shallow drainage and shelterbelts marking older parcels.",
    windNote:
      "Open prairie exposure with no terrain shelter; conditions here are typically stronger than in Calgary the same afternoon.",
    seasonalWindow:
      "Long, unobstructed light. Post-harvest gives the clearest read on boundaries and access.",
    bylawNote:
      "Rocky View County land-use rules apply; launch is arranged on the subject parcel with the owner's permission.",
    subjectMix: ["acreage-estate", "new-build-subdivision", "agricultural"],
    mobilisationNote:
      "About 35 minutes east of the Calgary base. Same-day capable.",
    faqs: [
      {
        question: "Do you need authorisation to fly a drone in Langdon?",
        answer:
          "Usually not. Langdon sits east of the Calgary International control zone in uncontrolled Class G airspace. The western edge of the county runs nearer the boundary, so the specific parcel is confirmed against NAV Drone first.",
      },
      {
        question: "What do acreage buyers want to see from the air near Langdon?",
        answer:
          "Access and boundaries. On flat prairie parcels the house is easy to photograph from the ground; what a buyer cannot verify is where the property line runs and how the parcel meets the road grid.",
      },
    ],
    primaryKeywords: [
      "drone photography Langdon",
      "Langdon acreage aerial photography",
      "Rocky View East drone video",
    ],
    nearestLocations: ["chestermere", "conrich", "indus", "strathmore", "calgary"],
  },

  {
    slug: "conrich",
    name: "Conrich",
    region: "rocky-view-east",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 51.0872, lng: -113.8489 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone vicinity",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 14,
      authorizationRequired: true,
      authorizationNote:
        "Conrich sits close to the eastern edge of the Calgary International control zone. Authorisation status is confirmed per site, and much of the area requires it.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Conrich has become one of the region's densest concentrations of industrial and logistics land, immediately east of the airport.",
      consequence:
        "Yard utilisation, trailer counts and laydown organisation are operational questions that a ground photograph cannot answer at all.",
      resolution:
        "A repeatable overhead pass turns a yard into a measurable asset — the same frame, the same altitude, on a schedule.",
    },
    landmarks: ["Conrich Road", "Highway 1", "Range Road 285", "the CN intermodal corridor"],
    terrain:
      "Flat industrial land east of the city, with large parcels, rail infrastructure and minimal natural relief.",
    windNote:
      "Exposed prairie with strong, steady flow and little shelter between yards.",
    seasonalWindow:
      "Year-round subject. Winter snow cover actually improves contrast between paved yard, stock and structure.",
    bylawNote:
      "Rocky View County industrial land-use rules apply; site launch requires the operator's permission.",
    subjectMix: ["industrial-yard", "construction-progress", "municipal-infrastructure"],
    mobilisationNote:
      "About 25 minutes east of the Calgary base, subject to the control-zone authorisation being in hand.",
    faqs: [
      {
        question: "Do you need authorisation to fly a drone in Conrich?",
        answer:
          "In most of the area, yes. Conrich sits close to the eastern edge of the Calgary International control zone. Flights are conducted under an Advanced RPAS certificate with authorisation filed before the shoot is confirmed.",
      },
      {
        question: "What does aerial documentation give an industrial yard operator?",
        answer:
          "Measurable state. Repeat passes flown from identical coordinates and altitude show yard utilisation, stock position and laydown organisation over time — questions that cannot be answered from a ground photograph at all.",
      },
    ],
    primaryKeywords: [
      "drone photography Conrich",
      "Conrich industrial aerial survey",
      "industrial yard drone photography Calgary",
    ],
    nearestLocations: ["chestermere", "langdon", "calgary", "indus", "airdrie"],
  },

  {
    slug: "indus",
    name: "Indus",
    region: "rocky-view-east",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 50.9394, lng: -113.7756 },
    tier: 4,
    driveMinutesFromCalgary: 30,
    airspace: {
      classification: "Class G uncontrolled, local aerodrome nearby",
      nearestAerodrome: "Kirkby Field (Indus)",
      distanceToAerodromeKm: 3,
      authorizationRequired: false,
      authorizationNote:
        "Indus sits in uncontrolled Class G airspace, but a private aerodrome operates nearby and its circuit is plotted before any local flight.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
      restrictedZones: ["Kirkby Field circuit"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Indus has an active private airfield in the middle of otherwise open uncontrolled farmland.",
      consequence:
        "A pilot who checks only for controlled airspace sees a clear result and misses the circuit traffic overhead entirely.",
      resolution:
        "Local aerodrome activity is plotted alongside the airspace check, because uncontrolled does not mean unoccupied.",
    },
    landmarks: ["Kirkby Field", "Highway 24", "Township Road 232", "the Bow River"],
    terrain:
      "Open agricultural land on the survey grid, with the Bow River corridor to the south-west providing the only relief.",
    windNote: "Open prairie exposure, steady and unbroken across the section grid.",
    seasonalWindow:
      "Cultivation cycle governs the look; post-harvest is clearest for boundary and access work.",
    bylawNote:
      "Rocky View County rules apply; launch is arranged on the subject parcel with the landowner's permission.",
    subjectMix: ["agricultural", "acreage-estate"],
    mobilisationNote: "About 30 minutes south-east of the Calgary base.",
    faqs: [
      {
        question: "Can you fly a drone near Indus?",
        answer:
          "Yes. Indus sits in uncontrolled Class G airspace, so no NAV CANADA authorisation is normally required. A private aerodrome operates nearby, so its circuit is plotted on the flight plan before takeoff.",
      },
      {
        question: "Does uncontrolled airspace mean there is no air traffic?",
        answer:
          "No, and that is a common and dangerous assumption. Uncontrolled means no clearance is required, not that nothing is flying. Private airfields like the one at Indus generate real circuit traffic in otherwise open airspace.",
      },
    ],
    primaryKeywords: [
      "drone photography Indus Alberta",
      "Indus agricultural aerial photography",
      "flying a drone near a private airfield Alberta",
    ],
    nearestLocations: ["langdon", "conrich", "chestermere", "de-winton", "strathmore"],
  },

  {
    slug: "de-winton",
    name: "De Winton",
    region: "foothills-south",
    city: "Foothills County",
    province: "Alberta",
    coordinates: { lat: 50.8319, lng: -113.9436 },
    tier: 4,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Okotoks Air Park",
      distanceToAerodromeKm: 12,
      authorizationRequired: false,
      authorizationNote:
        "De Winton sits in uncontrolled Class G airspace between Calgary's southern limit and Okotoks. The Calgary control-zone boundary lies north and is confirmed for parcels close to it.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "De Winton sits in the seam between Calgary's southern control-zone edge and the open foothills, which is why estate acreages cluster here.",
      consequence:
        "Parcels a few kilometres apart can fall on opposite sides of the authorisation line, and the address alone does not tell you which.",
      resolution:
        "The check is run from parcel coordinates at booking, so the answer is settled before the calendar is.",
    },
    landmarks: ["Highway 2A", "Dunbow Road", "the Bow River", "Heritage Pointe"],
    terrain:
      "Rolling foothills transition country south of Calgary, dropping toward the Bow River valley to the east.",
    windNote: "Foothills chinook exposure, building through the afternoon.",
    seasonalWindow:
      "Autumn shelterbelt colour separates estate parcels from surrounding pasture most clearly.",
    bylawNote:
      "Foothills County land-use rules apply; launch is arranged on the subject property with the owner's permission.",
    subjectMix: ["acreage-estate", "luxury-residential"],
    mobilisationNote: "About 25 minutes south of the Calgary base.",
    faqs: [
      {
        question: "Is De Winton inside controlled airspace?",
        answer:
          "Mostly not. De Winton sits south of the Calgary control-zone boundary in uncontrolled Class G airspace. Parcels closest to the northern edge are confirmed against NAV Drone individually rather than assumed.",
      },
      {
        question: "Why do estate acreages cluster around De Winton?",
        answer:
          "It is close enough to Calgary for a short commute and far enough out for large parcels. For aerial work that means the subject is almost always the land itself — boundary, approach and usable acreage rather than the building.",
      },
    ],
    primaryKeywords: [
      "drone photography De Winton",
      "De Winton acreage aerial photography",
      "Foothills County estate drone video",
    ],
    nearestLocations: ["okotoks", "priddis", "calgary", "aldersyde", "indus"],
  },

  {
    slug: "priddis",
    name: "Priddis",
    region: "foothills-south",
    city: "Foothills County",
    province: "Alberta",
    coordinates: { lat: 50.8733, lng: -114.3236 },
    tier: 4,
    driveMinutesFromCalgary: 35,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 21,
      authorizationRequired: false,
      authorizationNote:
        "Priddis sits in uncontrolled Class G airspace south-west of the Springbank control zone. The northern parcels are confirmed individually.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Priddis sits in the first real fold of the foothills, where the ground stops being prairie and starts having genuine relief.",
      consequence:
        "That relief hides property boundaries completely — a parcel can run over a ridge and out of sight from every point on the driveway.",
      resolution:
        "An altitude pass flattens the terrain into a readable plan view while a lower orbit preserves the sense of the fold, and both go in the same delivery.",
    },
    landmarks: ["Priddis Valley Road", "Highway 22", "the Priddis Greens", "Fish Creek"],
    terrain:
      "Genuine foothills relief — folded ridges and wooded draws, with parcels frequently running over more than one crest.",
    windNote:
      "Ridge-and-draw wind behaviour, with sharp local variation over short distances.",
    seasonalWindow:
      "Autumn colour in the draws gives the strongest separation; winter shows terrain shape most clearly under low sun.",
    bylawNote:
      "Foothills County rules apply; Kananaskis provincial land begins further west and carries separate restrictions.",
    subjectMix: ["acreage-estate", "resort-terrain"],
    mobilisationNote: "About 35 minutes south-west of the Calgary base on Highway 22.",
    faqs: [
      {
        question: "Can you fly a drone in Priddis?",
        answer:
          "Yes. Priddis sits in uncontrolled Class G airspace south-west of the Springbank control zone, so no authorisation is normally required. Northern parcels closest to the boundary are confirmed individually.",
      },
      {
        question: "How does foothills terrain change an acreage shoot?",
        answer:
          "It hides the parcel. Around Priddis a property can run over a ridge and be entirely invisible from the driveway. The delivery pairs a high plan view for boundaries with a lower orbit that keeps the sense of the terrain.",
      },
    ],
    primaryKeywords: [
      "drone photography Priddis",
      "Priddis foothills aerial photography",
      "Priddis acreage drone video",
    ],
    nearestLocations: ["bragg-creek", "de-winton", "okotoks", "elbow-valley", "millarville"],
  },
];
